import { resolveChartState } from '../../utils/chart-data-state.js'
import ChartState from './chart-state.vue'

/**
 * Gives a chart wrapper its non-data states: the `loading` / `error` /
 * `stateTitle` / `stateDescription` / `stateIcon` props, the resolved
 * `chartState`, and the `isPopulated` flag its template gates on. Also
 * registers `ChartState`.
 *
 * A factory rather than a plain mixin because the label rule differs by chart
 * type. Bar, line and radar position points along a category or radial scale
 * and draw nothing without labels. Pie, doughnut and polar-area size their
 * arcs from the values alone and use labels only for legend text, so they pass
 * `requireLabels: false`. See ADR-0012.
 *
 * The wrapper still owns its own template. It is expected to render its chart
 * generator under `v-if="isPopulated"` and `<ChartState v-else>` alongside,
 * forwarding `height`, `stateTitle`, `stateDescription`, `stateIcon` and
 * re-emitting `retry`.
 *
 * @param {Object} [options]
 * @param {boolean} [options.requireLabels=true] - False for slice-style charts
 * @returns {Object} A Vue 2 mixin
 */
export function createChartStateMixin ({ requireLabels = true, ghostShape = 'bar' } = {}) {
	return {
		components: {
			ChartState
		},
		props: {
			/** Whether the host is still fetching. Shows the loading state. */
			loading: {
				type: Boolean,
				default: false
			},
			/**
			 * Any truthy value marks the fetch as failed and shows the error
			 * state rather than the empty one. Withhold it if you would rather
			 * keep stale data on screen through a failed refetch.
			 */
			error: {
				type: [String, Object, Boolean, Error],
				default: null
			},
			/** Overrides the empty/error state title. */
			stateTitle: {
				type: String,
				default: null
			},
			/** Overrides the empty/error state description. */
			stateDescription: {
				type: String,
				default: null
			},
			/**
			 * Overrides the empty/error state glyph, as an `mdi-` name. Worth
			 * setting alongside `stateTitle` and `stateDescription`: the
			 * default glyph points at filters, so leaving it untouched while
			 * rewording the copy can contradict that wording.
			 */
			stateIcon: {
				type: String,
				default: null
			}
		},
		computed: {
			chartState () {
				return resolveChartState({
					loading: this.loading,
					error: this.error,
					chartData: this.chartData,
					requireLabels
				})
			},
			isPopulated () {
				return this.chartState === 'populated'
			},
			/**
			 * Which scaffold the empty and error states draw. A wrapper whose
			 * shape depends on its options rather than its type overrides this
			 * with its own computed; a component's own computed wins over a
			 * mixin's. `bar-chart` does exactly that for `indexAxis: 'y'`.
			 */
			ghostShape () {
				return ghostShape
			},
			/**
			 * How many series the chart would have drawn, so a multi-series
			 * chart is not stood in for by a single line. Taken from the
			 * payload rather than wired in per consumer, since the wrapper
			 * already has it.
			 */
			ghostSeries () {
				return this.chartData?.datasets?.length || 1
			}
		}
	}
}
